import { Injectable, inject } from '@angular/core'
import { TruckAndDevice } from '../classes/truck-and-device'
import { CycDatePipe } from 'projects/tools/src/lib/cyc-date.pipe'
import { TimePipe } from 'projects/tools/src/lib/time.pipe'
import { TruckConfig } from 'projects/appsync/src/public-api'
import { NUMBER_EMPTY, STRING_EMPTY } from '../constants/location'

@Injectable({
  providedIn: 'root',
})
export class TruckHelpersService {
  private date = inject(CycDatePipe)
  private timePipe = inject(TimePipe)

  public showLiveConsistence(device: TruckAndDevice): boolean {
    if (!device) {
      return false
    }
    if (device.hasConsistencePermission) {
      // User has overall consistence permission
      return true
    }
    // pour
    if (
      device.consistenceMmAtPour !== null &&
      device.hasConsistenceAtPourPermission
    ) {
      return true
    }
    // check the pre-pour consistence value
    if (device.consistenceMm === null) {
      return false
    }
    // site
    if (!!device.arrivedAtSite && device.hasConsistenceAtSiteEnterPermission) {
      return true
    }
    // left plant
    if (!!device.leftBatchPlant && device.hasConsistenceAtPlantExitPermission) {
      return true
    }
    return false
  }

  public parseTruckConfig(config: string): TruckConfig {
    try {
      const parsed = JSON.parse(config) as TruckConfig
      return parsed
    } catch (error) {
      return null
    }
  }

  public supportsConsistencePrediction(truck: TruckAndDevice): boolean {
    const config = truck?.truckConfig
    if (!config) {
      // If there is no truck config, assume the truck supports consistence prediction
      return true
    }
    if (!config.globalConfig) {
      return false
    }
    const supportsPrediction =
      typeof config.globalConfig.no_consistence === 'boolean'
        ? !config.globalConfig.no_consistence
        : true
    return supportsPrediction
  }

  public canDeliver(truck: TruckAndDevice) {
    const isWhitelisted =
      typeof truck?.truckConfig?.globalConfig?.is_whitelisted === 'boolean'
        ? truck?.truckConfig?.globalConfig?.is_whitelisted
        : true
    const isOffTheRoad =
      typeof truck?.truckConfig?.globalConfig?.is_off_the_road === 'boolean'
        ? truck?.truckConfig?.globalConfig?.is_off_the_road
        : false
    return isWhitelisted && !isOffTheRoad
  }

  public isInTransitWithNoDocket(selectedTruck: TruckAndDevice) {
    const truckIsAtGeofence = !!selectedTruck?.geofenceIdCurrent
    const truckHasDocketBase = !!selectedTruck?.plantCode
    // show all bases when no docket BASE assigned and truck is not in a goefence
    return selectedTruck && !truckHasDocketBase && !truckIsAtGeofence
  }

  public truckSort(a: TruckAndDevice, b: TruckAndDevice) {
    const aIsInactive = !a.isActiveToday
    const bIsInactive = !b.isActiveToday
    const aHasDocket = !!a.journeyProps?.docketId
    const bHasDocket = !!b.journeyProps?.docketId
    const bothHaveDocket = aHasDocket && bHasDocket
    const aDateBatch = a.journeyProps?.dateBatch
      ? a.journeyProps.dateBatch.getTime()
      : null
    const bDateBatch = b.journeyProps?.dateBatch
      ? b.journeyProps.dateBatch.getTime()
      : null
    const bothHaveBatch = aDateBatch && bDateBatch
    const aIsAtPlant = !!a.journeyProps?.isAtPlant
    const bIsAtPlant = !!b.journeyProps?.isAtPlant
    // favourites first
    if (a.favourite && !b.favourite) {
      return -1
    } else if (!a.favourite && b.favourite) {
      return 1
    }
    // separate active/inactive
    else if (aIsInactive && !bIsInactive) {
      return 1
    } else if (!aIsInactive && bIsInactive) {
      return -1
    } else if (!aHasDocket && bHasDocket) {
      // a has no docket and b does
      return 1
    } else if (aHasDocket && !bHasDocket) {
      // a has a docket and b does not
      return -1
    } else if (bothHaveDocket && !aDateBatch && bDateBatch) {
      // a has no Batch date and b does
      return 1
    } else if (bothHaveDocket && aDateBatch && !bDateBatch) {
      // a has a Batch date and b does not
      return -1
    } else if (bothHaveDocket && bothHaveBatch && aDateBatch < bDateBatch) {
      // a batched earlier than b
      return 1
    } else if (bothHaveDocket && bothHaveBatch && aDateBatch > bDateBatch) {
      // b batched earlier than a
      return -1
    } else if (!aIsAtPlant && bIsAtPlant) {
      // a is not at Plant and b is
      return 1
    } else if (aIsAtPlant && !bIsAtPlant) {
      // a is at Plant and b is not
      return -1
    }
    return 0
  }

  public cleanTruckInfo<T>(truckInfo: T): T {
    if (truckInfo) {
      Object.keys(truckInfo).forEach((key) => {
        const truckInfoObj = truckInfo as any
        const val = truckInfoObj[key]
        const valType = typeof val
        if (valType === 'string' && val === STRING_EMPTY) {
          truckInfoObj[key] = ''
        } else if (valType === 'string') {
          // e.g. 'S3   ' to 'S3'
          truckInfoObj[key] = (truckInfoObj[key] as string).trim()
        } else if (valType === 'number' && val === NUMBER_EMPTY) {
          truckInfoObj[key] = null
        }
      })
    }
    return truckInfo
  }

  public getTruckInfo(selectedTruck: TruckAndDevice) {
    if (!selectedTruck) {
      return ''
    }
    const truck = [
      `Truck: ${selectedTruck.truckRegistration}`,
      `TruckId: ${selectedTruck.truckId}`,
      `IMEI: ${selectedTruck.imei}`,
      `extTruckName: ${selectedTruck.extTruckName ?? ''}`,
      `extTruckId: ${selectedTruck.extTruckId ?? ''}`,
      `systemDatetimeUtcStr: ${this.dateAndTime(
        selectedTruck.systemDatetimeUtcStr,
      )}`,
      `consistenceMm: ${selectedTruck.consistenceMm}`,
      `consistenceMmQualifier: ${selectedTruck.consistenceMmQualifier}`,
      `consistenceMmAtLoadMix: ${selectedTruck.consistenceMmAtLoadMix}`,
      `consistenceMmAtPlantExit: ${selectedTruck.consistenceMmAtPlantExit}`,
      `consistenceMmAtSiteArrival: ${selectedTruck.consistenceMmAtSiteArrival}`,
      `consistenceMmAtPour: ${selectedTruck.consistenceMmAtPour}`,
      `temperatureModuleConcreteTemperatureDegc: ${selectedTruck.temperatureModuleConcreteTemperatureDegc}`,
      `geofenceIdCurrent: ${selectedTruck.geofenceIdCurrent}`,
      `plantGeofenceIdMostRecent: ${selectedTruck.plantGeofenceIdMostRecent}`,
      `tripStartTime: ${selectedTruck.tripStartTime ? this.dateAndTime(selectedTruck.tripStartTime) : ''}`,
      `tripEndTime: ${selectedTruck.tripEndTime ? this.dateAndTime(selectedTruck.tripEndTime) : ''}`,
      `deliveryStartTime: ${selectedTruck.deliveryStartTime ? this.dateAndTime(selectedTruck.deliveryStartTime) : ''}`,
      `deliveryEndTime: ${selectedTruck.deliveryEndTime ? this.dateAndTime(selectedTruck.deliveryEndTime) : ''}`,
      `datetimeBatch: ${selectedTruck.datetimeBatch}`,
      `leftBatchPlant: ${selectedTruck.leftBatchPlant}`,
      `arrivedAtSite: ${selectedTruck.arrivedAtSite}`,
      `pourTime: ${selectedTruck.pourTime}`,
      `pourLocation: ${selectedTruck.pourLocation}`,
      `pourEvents: ${selectedTruck.pourEvents}`,
      `leftSite: ${selectedTruck.leftSite}`,
    ]
    const tracking = [
      `inBatchPlant: ${selectedTruck.inBatchPlant}`,
      `inSite: ${selectedTruck.inSite}`,
      `canTrackGeo: ${selectedTruck.canTrackGeo}`,
    ]
    const docket = selectedTruck.docketId
      ? [
          `Delivery ID: ${selectedTruck.docketId}`,
          `Order ID: ${selectedTruck.orderCode}`,
          `Customer ID: ${selectedTruck.rmcCustomerId}`,
          `Batch Plant ID: ${selectedTruck.plantCode}`,
          `Customer: ${selectedTruck.rmcCustomerName}`,
          `Batch Plant: ${selectedTruck.plantName}`,
          `loadingPoint: ${selectedTruck.loadingPoint}`,
          `Delivery Site: ${selectedTruck.jobsiteDescription}`,
          `Volume: ${selectedTruck.deliveredQuantity}`,
          `Slump Code: ${selectedTruck.slumpCode}`,
          `Consistence Class: ${selectedTruck.consistenceClass}`,
          `Consistence Test Type: ${selectedTruck.consistenceTestType}`,
          `Consistence Prediction Type: ${selectedTruck.consistencePredictionType}`,
          `Consistence Class Min: ${selectedTruck.consistenceClassMin}`,
          `Consistence Class Max: ${selectedTruck.consistenceClassMax}`,
          `Consistence Lower Limit Spot: ${selectedTruck.consistenceLowerLimitSpot}`,
          `Consistence Upper Limit Spot: ${selectedTruck.consistenceUpperLimitSpot}`,
          `Docket Batch Time: ${this.dateAndTime(selectedTruck.datetimeBatch)}`,
          `Mix Design Code: ${selectedTruck.mixDesignCode}`,
          `Max Water Cement Ratio: ${selectedTruck.maxWaterCementRatio}`,
          `Allowable Water To Add: ${selectedTruck.allowableWaterToAddLitres}`,
        ]
      : ['** No Current Docket **']
    const journey = selectedTruck?.journeyProps
      ? [
          `Batch Time: ${this.dateAndTime(
            selectedTruck.journeyProps.dateBatch,
          )}`,
          `Batch Mixing: ${this.dateAndTime(
            selectedTruck.journeyProps.dateBatchMixing,
          )}`,
          `Plant Exit: ${this.dateAndTime(
            selectedTruck.journeyProps.datePlantLeave,
          )}`,
          `Plant Exit Slump: ${selectedTruck.journeyProps.consistenceMmAtPlantExit}`,
          `Site Enter: ${this.dateAndTime(
            selectedTruck.journeyProps.dateSiteArrive,
          )}`,
          `Site Enter Slump: ${selectedTruck.journeyProps.consistenceMmAtSiteArrival}`,
          `Pour Start: ${this.dateAndTime(
            selectedTruck.journeyProps.datePourStart,
          )}`,
          `Pour Start Slump: ${selectedTruck.journeyProps.consistenceMmAtPour}`,
          `Site Exit: ${this.dateAndTime(
            selectedTruck.journeyProps.dateSiteLeave,
          )}`,
          `Plant Enter: ${this.dateAndTime(
            selectedTruck.journeyProps.datePlantArrive,
          )}`,
          `Journey Stage: ${selectedTruck.journeyProps.stage}`,
        ]
      : ['** No Current Journey **']
    return [
      '-- TRUCK --',
      ...truck,
      '-- DOCKET --',
      ...docket,
      '-- JOURNEY --',
      ...journey,
      '-- TRACKING --',
      ...tracking,
    ].join('\n')
  }

  public dateAndTime(value: string | number | Date) {
    return value
      ? this.date.transform(value) + ' ' + this.timePipe.transform(value)
      : ''
  }
}
